import type {
  PostMemberOrderExtendPayload,
  PostOrderRes,
  ReqGuestOrderIdPayload,
  ReqMemberOrderIdPayload
} from '@/uTypes/order/api'
import type { MarketTabOrderDataType, PaymentData } from '@/uTypes/order'
import { ORDER_REDIRECT_URL, ALLOW_ORDER_SUCCESS } from '@/constants/order'
import { MARKET_TAB_ORDER, MARKET_TAB_USE_COUPON } from '@/constants/analytics/marketTab'
import { deepCopy, isObjectEqual, parseQueryString } from '@/utils/object'
import { useOrderAPI } from '@/uComposables/api/useOrderAPI'
import type TossPayments from '@/uComponents/order/TossPayments.vue'
import { useSingleButtonDialog } from '../modalControl/useSingleButtonDialog'

// const isNeedCancelCoupon = ref(false)
// const isNeedCancelPoint = ref(false)
const isProcessingPayment = ref<boolean>(false)

export const usePayment = () => {
  const route = useRoute()
  const { $swal } = useNuxtApp()
  const { open } = useSingleButtonDialog()
  const { message } = useAlarm()
  const analyticsDataStore = useAnalyticsDataStore()
  const userDataStore = useUserData()
  const { userData } = storeToRefs(userDataStore)

  const {
    postMemberOrder,
    postMemberMultiPrdOrder,
    postPartnersMemberOrder,
    postGuestOrder,
    postGuestMultiPrdOrder,
    postMemberExtendOrder,
    postMemberExtendOrderGroup,
    postMemberAddonOrder,
    postMemberAddonOrderGroup
  } = useOrderAPI()
  // 결제 수단 컴포넌트 ref
  const tossPaymentRef = ref<InstanceType<typeof TossPayments> | null>(null)
  // 중복 결제 요청 방지를 위한 이전 payload
  const prevPayload = ref<unknown | null>(null)
  // 이전 payload로 생성된 주문번호
  const prevOrderId = ref<string | null>(null)

  /**
   * orderId가 옛날 주문인지 확인
   * - O: 최신 주문의 orderGroupId
   * - P: 최신 주문 그룹 하위의 orderId
   * - U: 예전 주문의 orderId
   */
  const isOldOrder = (payload: unknown): boolean => {
    if (!payload || typeof payload !== 'object') return false

    const orderPayload = payload as {
      baseOrderId?: string
      orderId?: string
      supplierOrderId?: string
    }
    const orderId = orderPayload.baseOrderId ?? orderPayload.orderId ?? orderPayload.supplierOrderId

    return (
      (typeof orderId === 'string' && orderId.startsWith('U')) || !!orderPayload.supplierOrderId
    )
  }

  // 결제 진행중 버튼상태

  // 에러 케이스
  const ERROR_MESSAGE_MAP = [
    {
      keyword: '사용할 수 없는 쿠폰이에요',
      text: '만료되었거나 사용 조건이 맞지 않아요.',
      confirmFn: () => {
        // isNeedCancelCoupon.value = true
        window.location.reload()
      }
    },
    {
      keyword: '사용할 수 있는 적립금이 부족해요',
      text: '보유 적립금이 변동되었어요. 사용할 적립금을 다시 입력해 주세요.',
      confirmFn: () => {
        // isNeedCancelPoint.value = true
        window.location.reload()
      }
    },
    {
      keyword: '판매중인 상품이 아니에요',
      text: '품절되거나 판매가 종료된 상품이 포함되어 있어요.',
      confirmFn: () => {
        const prevPath = useCookie('prev_path')
        const path = prevPath.value
        // 쿠키삭제
        prevPath.value = null
        if (path?.length) {
          window.location.replace(path)
        } else {
          const partnerKey = route.params.partner as string | undefined
          window.location.replace(partnerKey ? `/partners/${partnerKey}` : '/')
        }
      }
    }
  ] as const

  const getErrorMessage = (message?: string) => {
    const matched = ERROR_MESSAGE_MAP.find(({ keyword }) => message?.includes(keyword))

    return {
      txt: matched?.text ?? message ?? '',
      confirm: matched?.confirmFn
    }
  }

  /**
   * 결제완료 후 애널리틱스 로그를 위한 결제정보 세팅
   * @param orderId - 주문 ID
   * @param amount - 결제 금액
   */
  const setAnalyticsCheckoutData = (
    orderId: string,
    amount: number,
    coupon?: SelectedCouponInfo
  ) => {
    useOrderData().orderId = orderId // guide/enroll페이지에서 사용하고 있어서 추가
    if (tossPaymentRef.value?.item) {
      analyticsDataStore.setCheckoutItem({
        ...tossPaymentRef.value.item,
        amount,
        orderId,
        simUsageType: tossPaymentRef.value.simUsageType,
        coupon,
        payMethod: tossPaymentRef.value.selectedPaymentMethod || 'NAVERPAY'
      })
    }
  }

  /**
   * 토스 결제 위젯을 호출하는 함수
   * @param orderId - 주문 ID
   * @param orderName - 결제 상품 이름
   * @param amount - 결제 금액
   * @param productPrice - 상품 가격
   * @param queryString - 전달할 추가 쿼리 문자열
   * @param customerInfo - 구매자 정보 (이름, 이메일, 휴대폰번호)
   */
  const initiateTossPayment = (
    orderId: string,
    orderName: string,
    amount: number,
    productPrice: number,
    queryString: string,
    selectedCouponInfo?: SelectedCouponInfo,
    customerInfo?: { name?: string; email?: string; mobile?: string }
  ) => {
    const payload: PaymentData = {
      orderId,
      orderName,
      amount,
      queryString,
      customerName: customerInfo?.name,
      customerEmail: customerInfo?.email,
      customerMobilePhone: customerInfo?.mobile
    }
    // 결제완료 후 애널리틱스 로그를 위한 결제정보 세팅
    setAnalyticsCheckoutData(orderId, amount, selectedCouponInfo)

    // 현재 경로를 저장해두었다가 결제 성공 페이지에서 결제 승인 api 실패시 replace로 되돌아오기 위해
    const currentUrl = sessionStorage.getItem(ORDER_REDIRECT_URL)
    if (!currentUrl) sessionStorage.setItem(ORDER_REDIRECT_URL, route.fullPath)

    if (tossPaymentRef.value?.isSelectNaverPay) {
      if (!tossPaymentRef.value.isAgreeRequiredTerms) {
        open({
          title: '결제를 위해 약관에 동의해 주세요.',
          description: '',
          buttonText: '확인',
          customClass: 'single-button-warning-dialog'
        })
        // $swal?.fire('결제를 위해 약관에 동의해 주세요.')
        return
      }
      tossPaymentRef.value?.requestNaverPay(payload)
      return
    }

    tossPaymentRef.value?.requestTossPayment(payload)
  }

  /**
   * 이전 payload와 현재 payload를 비교하여 동일 여부를 판단하고, 다를 경우 prev를 최신 값으로 갱신하는 함수
   * @param prev - 이전 payload를 저장할 Ref 객체
   * @param current - 현재 비교 대상 payload
   * @returns 이전 값과 동일하면 true, 다르면 false
   */
  const isSamePayload = <T>(prev: Ref<T | null>, current: T): boolean => {
    // 첫 비교인 경우: 현재 값을 저장하고 false 반환
    if (!prev.value) {
      prev.value = deepCopy(current)
      return false
    }

    // 이전 객체와 현재 객체가 동일한지 비교
    const isSame = isObjectEqual(prev.value, current)

    // 값이 다르면 최신 payload로 갱신
    if (!isSame) {
      prev.value = deepCopy(current)
    }
    return isSame
  }

  /**
   * 사용자를 성공 페이지로 이동시키는 함수
   * @param isGuest - 비회원 여부
   * @param simUsageType - 연장/충전 유형 ('extend' | 'addon' | '')
   * @param simType - 심 종류 ('esim' | 'usim' | '')
   * @param partnerKey - 파트너 키('toss')
   */
  const goToOrderSuccessPage = (
    isGuest: boolean,
    simUsageType: SimUsageType | '',
    simType: SimType | '',
    partnerKey: string,
    orderGroupId?: string
  ) => {
    let path = ''

    if (isGuest) {
      path = '/order/success/guest'
      if (orderGroupId) {
        path += `?orderGroupId=${orderGroupId}`
      }
      if (simType) {
        path += `${path.includes('?') ? '&' : '?'}simType=${simType}`
      }
    } else if (simUsageType === 'extend') {
      path = '/order/success/extend'
      if (orderGroupId) {
        path += `?orderGroupId=${orderGroupId}`
      }
    } else if (simUsageType === 'addon') {
      path = '/order/success/addon'
      if (orderGroupId) {
        path += `?orderGroupId=${orderGroupId}`
      }
    } else if (!simUsageType && simType) {
      path = `/order/success/${simType}`
      if (orderGroupId) {
        path += `?orderGroupId=${orderGroupId}`
      }
    }

    if (path) {
      // 결제 완료 페이지에서 뒤로가기로 결제 페이지의 접근을 막기 위한 플래그 설정
      sessionStorage.setItem(ALLOW_ORDER_SUCCESS, 'true')

      if (!partnerKey) {
        window.location.href = path
      } else {
        window.location.href = `/partners/${partnerKey}${path}`
      }
    }
  }

  /**
   * 결제 금액이 0원인 경우 토스 결제 없이 주문번호 API 요청 후 결제 완료 페이지 이동
   * @param payload - 결제 요청에 사용되는 데이터 객체
   * @param queryString - 토스 결제에 넘길 쿼리 문자열
   * @param postOrderFn - 주문번호를 요청하는 API 함수
   * @param marketTabData - 마켓탭 이벤트 데이터
   */
  const handleZeroPayment = async <T>(
    payload: T,
    queryString: string,
    postOrderFn: (payload: T) => Promise<PostOrderRes>,
    marketTabData?: MarketTabOrderDataType,
    selectedCouponInfo?: SelectedCouponInfo
  ) => {
    const isNpay = tossPaymentRef.value?.isSelectNaverPay
    const isSame = isSamePayload(prevPayload, { ...payload, pgType: isNpay ? 'NPAY' : 'TOSS' })
    // 동일 payload면 이전 주문번호 재사용
    if (isSame && prevOrderId.value) {
      setAnalyticsCheckoutData(prevOrderId.value, 0, selectedCouponInfo)
      goToOrderSuccessPageByQuery(queryString, marketTabData, prevOrderId.value)
      return
    }
    try {
      const res: PostOrderRes = await postOrderFn({
        ...payload,
        pgType: isNpay ? 'NPAY' : 'TOSS'
      })
      if (res?.data?.orderGroupId) {
        prevOrderId.value = res.data.orderGroupId
        setAnalyticsCheckoutData(res.data.orderGroupId, 0, selectedCouponInfo)
        goToOrderSuccessPageByQuery(queryString, marketTabData, res.data.orderGroupId)
      } else {
        prevOrderId.value = res.data.orderId
        setAnalyticsCheckoutData(res.data.orderId, 0, selectedCouponInfo)
        goToOrderSuccessPageByQuery(queryString, marketTabData, res.data.orderId)
      }
    } catch (e) {
      console.error('주문번호 요청 실패', e)
      const errorTitle = e?.error?.message
      const getError = e?.error.subMessage
      open({
        title: errorTitle,
        description: getError,
        onConfirm: getError.confirm,
        buttonText: '확인',
        customClass: 'single-button-warning-dialog'
      })
      // $swal?.fire(
      //   {
      //     title: e?.error?.message,
      //     text: errorDesc
      //   } || '다시 시도해주세요.'
      // )
      // $swal?.fire(e?.error?.message || '다시 시도해주세요.')
      isProcessingPayment.value = false
    }
  }

  const goToOrderSuccessPageByQuery = (
    queryString: string,
    marketTabData?: MarketTabOrderDataType,
    orderId?: string
  ) => {
    if (marketTabData && orderId) {
      marketTabData.mkt_order_id = orderId
      sessionStorage.setItem(MARKET_TAB_ORDER, JSON.stringify(marketTabData))
    }
    const parsedObj = parseQueryString(queryString)
    const isGuest = (parsedObj.isGuest ?? false) as boolean
    const simUsageType = (parsedObj.simUsageType ?? '') as SimUsageType | ''
    const simType = (parsedObj.simType ?? '') as SimType | ''
    const partnerKey = (parsedObj.partnersKey ?? '') as string
    goToOrderSuccessPage(isGuest, simUsageType, simType, partnerKey, orderId)
  }

  /**
   * 공통 결제 요청 처리 함수
   * 동일한 payload는 주문번호를 중복 호출하지 않고, 이전 orderId를 재사용하여 결제 요청
   */
  const handleOrderAndPayment = async <T extends { paidAmount: number; totalAmount: number }>(
    payload: T,
    tossOrderName: string,
    queryString: string,
    postOrderFn: (payload: T) => Promise<PostOrderRes>,
    selectedCouponInfo?: SelectedCouponInfo,
    marketTabData?: MarketTabOrderDataType
  ) => {
    // payload에서 구매자 정보 추출
    // 비회원의 경우: payload에 orderUserName, email, mobile 필드가 있음
    // 회원의 경우: useUserData 스토어에서 정보를 가져옴
    // 모바일 번호는 특수문자 제거 후 숫자만 전달
    const getMobileNumber = (mobile: string | undefined): string | undefined => {
      if (!mobile) return undefined
      return mobile.replace(/[^0-9]/g, '')
    }

    const customerInfo =
      'orderUserName' in payload || 'email' in payload || 'mobile' in payload
        ? {
            // 비회원: payload에서 정보 추출
            name: 'orderUserName' in payload ? (payload as any).orderUserName : undefined,
            email: 'email' in payload ? (payload as any).email : undefined,
            mobile: getMobileNumber('mobile' in payload ? (payload as any).mobile : undefined)
          }
        : userData.value
          ? {
              // 회원: useUserData 스토어에서 정보 추출
              name: userData.value.userName,
              email: userData.value.email,
              mobile: getMobileNumber(userData.value.mobile)
            }
          : undefined
    const isNpay = tossPaymentRef.value?.isSelectNaverPay
    const isSame = isSamePayload(prevPayload, {
      ...payload,
      pgType: isNpay ? 'NPAY' : 'TOSS'
    })

    // 쿠폰사용한 경우, 결제 완료 후 실행 할 마켓탭 쿠폰 사용 이벤트 데이터 세팅
    if (selectedCouponInfo?.couponId) {
      sessionStorage.setItem(MARKET_TAB_USE_COUPON, JSON.stringify(selectedCouponInfo))
    }

    // 결제 금액이 0원인 경우
    if (payload.paidAmount === 0) {
      await handleZeroPayment(payload, queryString, postOrderFn, marketTabData, selectedCouponInfo)
      return
    }

    // Npay 결제 금액이 10원 미만인 경우
    if (payload.paidAmount < 10 && isNpay) {
      message('info', 'Npay 결제는 10원부터 가능해요.', false, false, 2000, 90, 'message-plain')
      isProcessingPayment.value = false
      return
    }

    // 동일한 payload면 이전 주문번호로 결제 시도
    if (isSame && prevOrderId.value) {
      initiateTossPayment(
        prevOrderId.value,
        tossOrderName,
        payload.paidAmount,
        payload.totalAmount,
        queryString,
        selectedCouponInfo,
        customerInfo
      )
      return
    }

    // 새로운 payload인 경우 주문번호 요청 후 결제 실행
    try {
      const res: PostOrderRes = await postOrderFn({
        ...payload,
        pgType: isNpay ? 'NPAY' : 'TOSS'
      })
      if (res?.data?.orderGroupId) {
        const orderId = res.data.orderGroupId
        prevOrderId.value = orderId
        // 결제 완료 후 실행 할 마켓탭 이벤트 데이터 세팅
        if (marketTabData) {
          marketTabData.mkt_order_id = orderId
          sessionStorage.setItem(MARKET_TAB_ORDER, JSON.stringify(marketTabData))
        }

        initiateTossPayment(
          orderId,
          tossOrderName,
          payload.paidAmount,
          payload.totalAmount,
          queryString,
          selectedCouponInfo,
          customerInfo
        )
      } else {
        // 파트너스는 orderGroupId이 없음
        const orderId = res.data.orderId
        prevOrderId.value = orderId
        // 결제 완료 후 실행 할 마켓탭 이벤트 데이터 세팅
        if (marketTabData) {
          marketTabData.mkt_order_id = orderId
          sessionStorage.setItem(MARKET_TAB_ORDER, JSON.stringify(marketTabData))
        }

        initiateTossPayment(
          orderId,
          tossOrderName,
          payload.paidAmount,
          payload.totalAmount,
          queryString,
          selectedCouponInfo,
          customerInfo
        )
      }
    } catch (e) {
      console.error('주문번호 요청 실패', e)
      const errorTitle = e?.error?.message
      const getError = e?.error.subMessag
      open({
        title: errorTitle,
        description: getError,
        buttonText: '확인',
        customClass: 'single-button-warning-dialog',
        onConfirm: getError.confirm
      })
      // $swal?.fire(
      //   {
      //     title: e?.error.message,
      //     text: errorDesc
      //   } || '다시 시도해주세요.'
      // )
      // $swal?.fire(e?.error.message || '다시 시도해주세요.')
      isProcessingPayment.value = false
    }
  }

  /**
   * 회원 주문번호를 요청하고, 성공 시 토스 결제를 호출
   * @param payload - 주문 생성 요청 페이로드
   * @param tossOrderName - 토스에 표시할 주문 이름
   * @param queryString - 토스 결제에 넘길 쿼리 문자열
   * @param marketTabData - 마켓탭 이벤트 데이터
   * @param selectedCouponInfo - 사용한 쿠폰 정보
   */
  const requestMemberOrder = (
    payload: ReqMemberOrderIdPayload,
    tossOrderName: string,
    queryString: string,
    marketTabData?: MarketTabOrderDataType,
    selectedCouponInfo?: SelectedCouponInfo
  ) => {
    return handleOrderAndPayment(
      payload,
      tossOrderName,
      queryString,
      postMemberMultiPrdOrder,
      selectedCouponInfo,
      marketTabData
    )
  }

  /**
   * 파트너스에서 회원 주문번호를 요청하고, 성공 시 토스 결제를 호출
   * @param payload - 주문 생성 요청 페이로드
   * @param tossOrderName - 토스에 표시할 주문 이름
   * @param queryString - 토스 결제에 넘길 쿼리 문자열
   * @param marketTabData - 마켓탭 이벤트 데이터
   * @param selectedCouponInfo - 사용한 쿠폰 정보
   */
  const requestPartnersMemberOrder = (
    payload: ReqMemberOrderIdPayload,
    tossOrderName: string,
    queryString: string,
    marketTabData?: MarketTabOrderDataType,
    selectedCouponInfo?: SelectedCouponInfo
  ) =>
    handleOrderAndPayment(
      payload,
      tossOrderName,
      queryString,
      postPartnersMemberOrder,
      selectedCouponInfo,
      marketTabData
    )

  /**
   * 비회원 주문번호를 요청하고, 성공 시 토스 결제를 호출
   * @param payload - 주문 생성 요청 페이로드
   * @param tossOrderName - 토스에 표시할 주문 이름
   * @param queryString - 토스 결제에 넘길 쿼리 문자열
   * @param marketTabData - 마켓탭 이벤트 데이터
   */
  const requestGuestOrder = (
    payload: ReqGuestOrderIdPayload,
    tossOrderName: string,
    queryString: string,
    marketTabData?: MarketTabOrderDataType,
    selectedCouponInfo?: SelectedCouponInfo
  ) =>
    handleOrderAndPayment(
      payload,
      tossOrderName,
      queryString,
      postGuestMultiPrdOrder,
      selectedCouponInfo,
      marketTabData
    )
  const requestPartnersGuestOrder = (
    payload: ReqGuestOrderIdPayload,
    tossOrderName: string,
    queryString: string,
    marketTabData?: MarketTabOrderDataType,
    selectedCouponInfo?: SelectedCouponInfo
  ) =>
    handleOrderAndPayment(
      payload,
      tossOrderName,
      queryString,
      postGuestOrder,
      selectedCouponInfo,
      marketTabData
    )

  /**
   * 회원 연장 주문번호를 요청하고, 성공 시 토스 결제를 호출
   * @param payload - 주문 생성 요청 페이로드
   * @param tossOrderName - 토스에 표시할 주문 이름
   * @param queryString - 토스 결제에 넘길 쿼리 문자열
   */
  const requestMemberExtendOrder = (
    payload: PostMemberOrderExtendPayload,
    tossOrderName: string,
    queryString: string,
    selectedCouponInfo?: SelectedCouponInfo
  ) => {
    const postOrderFn = isOldOrder(payload) ? postMemberExtendOrder : postMemberExtendOrderGroup

    return handleOrderAndPayment(
      payload,
      tossOrderName,
      queryString,
      postOrderFn,
      selectedCouponInfo
    )
  }

  /**
   * 회원 충전 주문번호를 요청하고, 성공 시 토스 결제를 호출
   * @param payload - 주문 생성 요청 페이로드
   * @param tossOrderName - 토스에 표시할 주문 이름
   * @param queryString - 토스 결제에 넘길 쿼리 문자열
   */
  const requestMemberAddonOrder = (
    payload: PostMemberOrderAddonPayload,
    tossOrderName: string,
    queryString: string,
    selectedCouponInfo?: SelectedCouponInfo
  ) => {
    const postOrderFn = isOldOrder(payload) ? postMemberAddonOrder : postMemberAddonOrderGroup

    return handleOrderAndPayment(
      payload,
      tossOrderName,
      queryString,
      postOrderFn,
      selectedCouponInfo
    )
  }

  return {
    tossPaymentRef,
    requestMemberOrder,
    requestPartnersMemberOrder,
    requestGuestOrder,
    requestPartnersGuestOrder,
    requestMemberExtendOrder,
    requestMemberAddonOrder,
    goToOrderSuccessPage,
    isProcessingPayment,
    isOldOrder
    // isNeedCancelCoupon,
    // isNeedCancelPoint
  }
}
